/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description constants
 */

import _ from 'lodash';

const APP_FEATURES: Record<App.Feature, App.Feature> = {
	default: 'default',
	codeexplorer: 'codeexplorer',
};

const FEATURES = {
	eligibility: {
		key: 'eligibility' as Menu.Feature,
		path: 'eligibility' as Menu.Feature,
		title: 'Eligibility',
	},
	claims: {
		key: 'claims' as Menu.Feature,
		path: 'claims' as Menu.Feature,
		title: 'Claims',
	},
	payments: {
		key: 'payments' as Menu.Feature,
		path: 'payments' as Menu.Feature,
		title: 'Payments',
	},
	codeExplorer: {
		key: 'code-explorer' as Menu.Feature,
		path: 'code-explorer' as Menu.Feature,
		title: 'Code Explorer',
	},
	settings: {
		key: 'settings' as Menu.Feature,
		path: 'settings' as Menu.Feature,
		title: 'Settings',
	},
	help: {
		key: 'help' as Menu.Feature,
		path: 'help' as Menu.Feature,
		title: 'Help',
	},
};

const ONBOARDING_FEATURES: Menu.Feature[] = [FEATURES.eligibility.key, FEATURES.claims.key, FEATURES.payments.key];

const SETTINGS_SECTIONS = {
	Organization: 'organization',
	DataManagement: 'data-management',
	Integration: 'integration',
};

/**
 * Page keys for the app
 */
const PAGE_KEYS = {
	eligibility: {
		section: FEATURES.eligibility.key,
		// Pages
		responses: `${FEATURES.eligibility.key}-responses`,
		newRequest: `${FEATURES.eligibility.key}-new-request`,
		editRequest: `${FEATURES.eligibility.key}-edit-request`,
		viewResponse: `${FEATURES.eligibility.key}-view-response`,
		batch: {
			add: `${FEATURES.eligibility.key}-add-batch`,
			edit: `${FEATURES.eligibility.key}-edit-batch`,
			viewResponse: `${FEATURES.eligibility.key}-view-batch-response`,
		},
	},
	claims: {
		section: FEATURES.claims.key,
		// Pages
		responses: `${FEATURES.claims.key}-responses`,
		newRequest: `${FEATURES.claims.key}-new-request`,
		viewResponse: `${FEATURES.claims.key}-view-response`,
		editRequest: `${FEATURES.claims.key}-edit-request`,
		// worklist
		worklist: {
			listView: `${FEATURES.claims.key}-worklist`,
			add: `${FEATURES.claims.key}-worklist-add`,
			addManual: `${FEATURES.claims.key}-worklist-add-manual`,
			edit: `${FEATURES.claims.key}-worklist-edit`,
			viewResponse: `${FEATURES.claims.key}-worklist-view-response`,
		},
	},
	payments: {
		section: FEATURES.payments.key,
		// Pages
		submissions: `${FEATURES.payments.key}-submissions`,
		quickPay: `${FEATURES.payments.key}-quick-pay`,
		request: `${FEATURES.payments.key}-request`,
	},
	codeExplorer: {
		section: FEATURES.codeExplorer.key,
		// Pages
		allCodes: `${FEATURES.codeExplorer.key}-all-codes`,
		searchResults: `${FEATURES.codeExplorer.key}-search-results`,
		codeSetSearchResults: `${FEATURES.codeExplorer.key}-code-set-search-results`,
		codeDetails: `${FEATURES.codeExplorer.key}-code-details`,
		favoriteCodes: `${FEATURES.codeExplorer.key}-favorite-codes`,
		codeList: `${FEATURES.codeExplorer.key}-code-list`,
	},
	help: {
		landing: FEATURES.help.key,
	},
	settings: {
		organization: {
			section: SETTINGS_SECTIONS.Organization,
			// Pages
			general: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-general`,
			subscriptions: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-subscriptions`,
			auditLogs: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-audit-logs`,
			users: {
				listView: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-users`,
				add: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-add-user`,
				edit: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Organization}-edit-user`,
			},
		},
		dataManagement: {
			section: SETTINGS_SECTIONS.DataManagement,
			// Pages
			providers: {
				listView: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-providers`,
				add: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-add-provider`,
				edit: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-provider`,
			},
			locations: {
				listView: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-locations`,
				add: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-add-location`,
				edit: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-location`,
			},
			payers: {
				listView: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-payers`,
				edit: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.DataManagement}-edit-payer`,
			},
		},
		integration: {
			section: SETTINGS_SECTIONS.Integration,
			// Pages
			payment: `${FEATURES.settings.key}-${SETTINGS_SECTIONS.Integration}-payment`,
		},
	},
};

// TODO - define menu config attribute if this feature is retained
const ONBOARDING_SETTINGS_PAGES: string[] = [
	..._.values(PAGE_KEYS.settings.dataManagement.providers),
	..._.values(PAGE_KEYS.settings.dataManagement.locations),
	..._.values(PAGE_KEYS.settings.dataManagement.payers),
	PAGE_KEYS.settings.integration.payment,
];

// TODO - add icons in next release
const ICON_MAP = {
	default: '' as Menu.IconType,
	eligibility: {
		responses: 'PeopleSafe' as Menu.IconType,
		newRequest: 'PeopleSafe' as Menu.IconType,
		editRequest: 'PeopleSafe' as Menu.IconType,
		viewResponse: 'PeopleSafe' as Menu.IconType,
		batch: {
			add: 'PeopleSafe' as Menu.IconType,
			edit: 'PeopleSafe' as Menu.IconType,
			viewResponse: 'PeopleSafe' as Menu.IconType,
		},
	},
	claims: {
		responses: 'Claims' as Menu.IconType,
		newRequest: 'Claims' as Menu.IconType,
		viewResponse: 'Claims' as Menu.IconType,
		editRequest: 'Claims' as Menu.IconType,
		worklist: {
			listView: 'ListBottom' as Menu.IconType,
			add: 'ListBottom' as Menu.IconType,
			addManual: 'ListBottom' as Menu.IconType,
			edit: 'ListBottom' as Menu.IconType,
			viewResponse: 'ListBottom' as Menu.IconType,
		},
	},
	payments: {
		submissions: 'Wallet' as Menu.IconType,
		quickPay: 'Wallet' as Menu.IconType,
		request: 'Wallet' as Menu.IconType,
	},
	codeExplorer: {
		allCodes: 'AllCodes' as Menu.IconType,
		searchResults: 'AllCodes' as Menu.IconType,
		codeDetails: 'AllCodes' as Menu.IconType,
		favoriteCodes: 'FileFocus' as Menu.IconType,
		codeList: 'AllCodes' as Menu.IconType,
	},
	settings: {
		organization: {
			general: '' as Menu.IconType,
			subscriptions: '' as Menu.IconType,
			auditLogs: '' as Menu.IconType,
			users: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
		},
		dataManagement: {
			providers: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
			locations: {
				listView: '' as Menu.IconType,
				add: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
			payers: {
				listView: '' as Menu.IconType,
				edit: '' as Menu.IconType,
			},
		},
		integration: {
			payment: '' as Menu.IconType,
		},
	},
	help: {
		landing: '' as Menu.IconType,
	},
};

/**
 * Routes for the app
 * Dynamic required param like /:id in the url path must be only added in extra routes
 * Dynamic optional param like /:tab? in the url path can be added in routes (pages)
 */
const ROUTES = {
	home: {
		landing: '/',
		match: '*',
	},
	logout: {
		landing: '/logout',
		redirect: ':origin?/logout',
	},
	eligibility: {
		landing: `/${FEATURES.eligibility.path}`,
		responses: `/${FEATURES.eligibility.path}/responses/:tab?`,
		newRequest: `/${FEATURES.eligibility.path}/new-request`,
		editRequest: `/${FEATURES.eligibility.path}/request/:id`,
		viewResponse: `/${FEATURES.eligibility.path}/response/:id`,
		batch: {
			add: `/${FEATURES.eligibility.path}/upload-batch/:id?`,
			edit: `/${FEATURES.eligibility.path}/batch/:id`,
			viewResponse: `/${FEATURES.eligibility.path}/batch/response/:id`,
		},
	},
	claims: {
		landing: `/${FEATURES.claims.path}`,
		responses: `/${FEATURES.claims.path}/responses`,
		viewResponse: `/${FEATURES.claims.path}/response/:id`,
		newRequest: `/${FEATURES.claims.path}/new-request`,
		editRequest: `/${FEATURES.claims.path}/request/:id`,
		// worklist
		worklist: {
			listView: `/${FEATURES.claims.path}/worklists/:tab?`,
			add: `/${FEATURES.claims.path}/create-worklist`,
			addManual: `/${FEATURES.claims.path}/create-manual-worklist`,
			edit: `/${FEATURES.claims.path}/worklist/:id`,
			viewResponse: `/${FEATURES.claims.path}/worklist/response/:id`,
		},
	},
	help: {
		landing: `/${FEATURES.help.path}`,
	},
	payments: {
		landing: `/${FEATURES.payments.path}`,
		submissions: `/${FEATURES.payments.path}/submissions`,
		quickPay: `/${FEATURES.payments.path}/quick-pay`,
		request: `/${FEATURES.payments.path}/new-request`,
	},
	codeExplorer: {
		landing: `/${FEATURES.codeExplorer.path}`,
		allCodes: `/${FEATURES.codeExplorer.path}/all-codes`,
		searchResults: `/${FEATURES.codeExplorer.path}/search/:searchTerm?`,
		codeSetSearchResults: `/${FEATURES.codeExplorer.path}/search/:codeSet/:searchTerm?`,
		codeDetails: `/${FEATURES.codeExplorer.path}/code/:codeSet/:code/:tab?`,
		favoriteCodes: `/${FEATURES.codeExplorer.path}/favorites/:searchTerm?`,
		codeList: `/${FEATURES.codeExplorer.path}/code-list/:codeSet/:searchTerm?`,
	},
	settings: {
		landing: `/${FEATURES.settings.path}`,
		match: `/${FEATURES.settings.path}/*`,
		organization: {
			landing: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}`,
			general: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/general`,
			subscriptions: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/subscriptions/:tab?`,
			auditLogs: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/audit-logs`,
			users: {
				listView: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/users`,
				add: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/add-user`,
				edit: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Organization}/user/:id`,
			},
		},
		dataManagement: {
			landing: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}`,
			providers: {
				listView: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/providers`,
				add: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/add-provider`,
				edit: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/provider/:id`,
			},
			locations: {
				listView: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/locations`,
				add: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/add-location`,
				edit: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/location/:id`,
			},
			payers: {
				listView: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/payers`,
				edit: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.DataManagement}/payer/:id/:tab?`,
			},
		},
		integration: {
			landing: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Integration}`,
			payment: `/${FEATURES.settings.path}/${SETTINGS_SECTIONS.Integration}/payment`,
		},
	},
};

const PAGE_TITLE = {
	eligibility: {
		responses: 'Responses',
		newRequest: 'New Request',
		viewResponse: 'Eligibility Response',
		editRequest: 'Edit Eligibility Request',
		batch: {
			add: 'New Batch',
			edit: 'Edit Batch',
			viewResponse: 'Batch Response',
		},
	},
	claims: {
		responses: 'All Claims',
		newRequest: 'Check New Status',
		viewResponse: 'Claim Status Response',
		editRequest: 'Edit Claim Status',
		worklist: {
			listView: 'Worklists',
			add: 'Create Automated Worklist',
			addManual: 'Create Manual Worklist',
			edit: 'Edit Worklist',
			viewResponse: 'Worklist Response',
		},
	},
	payments: {
		submissions: 'Payments',
		quickPay: 'Quick Pay',
		request: 'New Request',
	},
	codeExplorer: {
		allCodes: 'All Codes',
		searchResults: 'Search Results',
		codeDetails: 'Code Details',
		favoriteCodes: 'Favorites',
		codeList: 'ICD-10-PCS Code Lookup', // TODO : add dynamic title later
	},

	help: {
		landing: 'Help/FAQs',
	},
	settings: {
		organization: {
			general: 'General',
			subscriptions: 'Subscription',
			auditLogs: 'Audit Log',
			users: {
				listView: 'Users',
				add: 'Add User',
				edit: 'Edit User',
			},
		},
		dataManagement: {
			providers: {
				listView: 'Providers',
				add: 'Add Provider',
				edit: 'Edit Provider',
			},
			locations: {
				listView: 'Locations',
				add: 'Add Location',
				edit: 'Edit Location',
			},
			payers: {
				listView: 'Payers',
				edit: 'Edit Payer',
			},
		},
	},
};

const SEARCH_META = {
	default: {
		title: '',
		description: '',
		disable: false,
	} as Menu.SearchMeta,
	// Eligibility
	eligibility: {
		responses: {
			title: 'Eligibility Responses',
			description: 'View detailed responses of submitted eligibility requests',
			disable: false,
		} as Menu.SearchMeta,
		newRequest: {
			title: 'New Eligibility Request',
			description: 'Submit a new eligibility check for a single patient',
			disable: false,
		} as Menu.SearchMeta,
		viewResponse: {
			title: PAGE_TITLE.eligibility.viewResponse,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		editRequest: {
			title: PAGE_TITLE.eligibility.editRequest,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		// Batch
		batch: {
			add: {
				title: 'New Batch Eligibility Request',
				description: 'Submit eligibility checks for multiple patients at once by uploading a batch file',
				disable: false,
			} as Menu.SearchMeta,
			edit: {
				title: PAGE_TITLE.eligibility.batch.edit,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
			viewResponse: {
				title: PAGE_TITLE.eligibility.batch.viewResponse,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
		},
	},
	// Claims
	claims: {
		newRequest: {
			title: 'New Claim Status Check',
			description: 'Check the status of a submitted insurance claim',
			disable: false,
		} as Menu.SearchMeta,
		responses: {
			title: PAGE_TITLE.claims.responses,
			description: 'View detailed responses of submitted claims',
			disable: false,
		} as Menu.SearchMeta,
		viewResponse: {
			title: PAGE_TITLE.claims.viewResponse,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		editRequest: {
			title: PAGE_TITLE.claims.editRequest,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		// worklist
		worklist: {
			listView: {
				title: PAGE_TITLE.claims.worklist.listView,
				description: '',
				disable: false,
			} as Menu.SearchMeta,
			add: {
				title: PAGE_TITLE.claims.worklist.add,
				description: '',
				disable: false,
			} as Menu.SearchMeta,
			addManual: {
				title: PAGE_TITLE.claims.worklist.addManual,
				description: '',
				disable: false,
			} as Menu.SearchMeta,
			edit: {
				title: PAGE_TITLE.claims.worklist.edit,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
			viewResponse: {
				title: PAGE_TITLE.claims.worklist.viewResponse,
				description: '',
				disable: true,
			} as Menu.SearchMeta,
		},
	},
	payments: {
		submissions: {
			title: PAGE_TITLE.payments.submissions,
			description: 'View and manage payments',
			disable: false,
		} as Menu.SearchMeta,
		quickPay: {
			title: PAGE_TITLE.payments.quickPay,
			description: 'Quickly pay a claim',
			disable: false,
		} as Menu.SearchMeta,
		request: {
			title: PAGE_TITLE.payments.request,
			description: 'Submit a new payment request',
			disable: false,
		} as Menu.SearchMeta,
	},

	// Code Explorer
	codeExplorer: {
		allCodes: {
			title: 'Code Explorer - All Codes',
			description: 'Search across all ICD-10, CPT, and HCPCS codes',
			disable: false,
		} as Menu.SearchMeta,
		searchResults: {
			title: PAGE_TITLE.codeExplorer.searchResults,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		codeDetails: {
			title: PAGE_TITLE.codeExplorer.codeDetails,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
		favoriteCodes: {
			title: 'Code Explorer - Favorite Codes',
			description: `View the codes you've marked as 'favorites'`,
			disable: false,
		} as Menu.SearchMeta,
		codeList: {
			title: PAGE_TITLE.codeExplorer.codeList,
			description: '',
			disable: true,
		} as Menu.SearchMeta,
	},

	// Help
	help: {
		landing: {
			title: PAGE_TITLE.help.landing,
			description: 'Help & FAQs, Payment questions, Support, Contact us',
			disable: false,
		} as Menu.SearchMeta,
	},
	// Settings
	settings: {
		organization: {
			general: {
				title: 'Organization Settings',
				description: 'Update and manage organization-wide settings',
				disable: false,
			} as Menu.SearchMeta,
			subscriptions: {
				title: 'Subscription Details',
				description: 'View your subscription plan and billing information',
				disable: false,
			} as Menu.SearchMeta,
			auditLogs: {
				title: 'Audit Log',
				description: 'Track and review system activity and changes',
				disable: false,
			} as Menu.SearchMeta,
			// Users
			users: {
				listView: {
					title: 'User Management',
					description: 'View and manage user accounts and roles',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.organization.users.add,
					description: 'Add a new user to the organization and assign roles',
					disable: false,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.organization.users.edit,
					description: '',
					disable: true,
				} as Menu.SearchMeta,
			},
		},
		// Data Management
		dataManagement: {
			providers: {
				listView: {
					title: 'Provider Settings',
					description: 'View and manage provider information',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.dataManagement.providers.add,
					description: 'Add a new provider to your organization',
					disable: false,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.providers.edit,
					description: 'Manage Provider status, Identity Verification, Enrollment Specific Information',
					disable: true,
				} as Menu.SearchMeta,
			},
			locations: {
				listView: {
					title: 'Location Settings',
					description: 'View and manage organization locations',
					disable: false,
				} as Menu.SearchMeta,
				add: {
					title: PAGE_TITLE.settings.dataManagement.locations.add,
					description: 'Add a new location to your organization',
					disable: false,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.locations.edit,
					description: 'Manage Location, Status, Address, Contact Info',
					disable: true,
				} as Menu.SearchMeta,
			},
			payers: {
				listView: {
					title: 'Payer Settings',
					description: 'View and manage insurance payers',
					disable: false,
				} as Menu.SearchMeta,
				edit: {
					title: PAGE_TITLE.settings.dataManagement.payers.edit,
					description: 'Manage Enrollment, Contact info and Maintenance info',
					disable: true,
				} as Menu.SearchMeta,
			},
		},
		integration: {
			payment: {
				title: 'Payment Gateway Settings',
				description: 'Manage payment gateway settings',
				disable: false,
			} as Menu.SearchMeta,
		},
	},
};

export { APP_FEATURES, FEATURES, ROUTES, PAGE_KEYS, ICON_MAP, SEARCH_META, PAGE_TITLE, ONBOARDING_FEATURES, ONBOARDING_SETTINGS_PAGES };
